/**
 * Helpers puros da renderizacao hierarquica do PermissionMatrix
 * (Modulo > Pagina > Secao > Funcao). Sem estado, sem side effects —
 * facil de testar e de raciocinar.
 *
 * Shape esperado (campo `modulos` do catalogo, retrocompat com `grupos`):
 *   modulos: [{ id, label, paginas: [{ id, label, secoes: [{ id, label,
 *     permissoes: [{ codigo, acao, rotulo, descricao, requer_mfa,
 *                    ajuda: { rotulo, explicacao, exemplo, veja_tambem } }] }] }] }]
 */

// Rótulo CURTO por ação — só o último recurso, para permissão que ainda não
// tem `rotulo` próprio vindo do backend (permissoes/ajuda). A ação é a mesma
// para todos os recursos, então o verbo sozinho é ambíguo; por isso o
// backend manda um rótulo por permissão.
const ACAO_ROTULO_CURTO = {
  view: "Ver",
  create: "Criar",
  edit: "Editar",
  delete: "Excluir",
  approve: "Aprovar",
  cancel: "Cancelar",
  manage: "Gerenciar",
  autorizar: "Autorizar",
  atender: "Atender",
  gerar_lote: "Gerar lote",
  importar: "Importar",
  configurar: "Configurar",
  emitir: "Emitir",
  imprimir: "Imprimir",
  exportar: "Exportar",
};

export function rotuloPermissao(p) {
  return p?.rotulo || ACAO_ROTULO_CURTO[p?.acao] || p?.acao_label || p?.acao || p?.codigo;
}

/** Minúsculas e sem acento: "Aprovação" casa com "aprovacao". */
export function normalizarBusca(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

function textoCasa(valor, termo) {
  return normalizarBusca(valor).includes(termo);
}

function permissaoCasa(p, termo) {
  return [
    p.codigo,
    p.rotulo,
    p.descricao,
    p.acao_label,
    p.ajuda?.explicacao,
  ].some((v) => textoCasa(v, termo));
}

export function permissoesDaPagina(pagina) {
  return (pagina?.secoes || []).flatMap((s) => s.permissoes || []);
}

export function permissoesDoModulo(modulo) {
  return (modulo?.paginas || []).flatMap(permissoesDaPagina);
}

/** Concedida = grant explicito OU herdada sem revoke ativo. */
export function estaConcedida(codigo, { selecionados, herdados, revogados }) {
  return selecionados.has(codigo) || (herdados.has(codigo) && !revogados.has(codigo));
}

/**
 * Conta quantas permissoes estao efetivamente concedidas. Eh o numero que o
 * gestor entende como "o que esse modulo/pagina ja tem".
 */
export function contarConcedidas(permissoes, sets) {
  return permissoes.filter((p) => estaConcedida(p.codigo, sets)).length;
}

/**
 * Mantem so os niveis que passam em `aceita(permissao, secao, pagina, modulo)`.
 * Niveis sem permissao restante somem.
 */
function podarArvore(modulos, aceita) {
  return modulos
    .map((modulo) => {
      const paginas = (modulo.paginas || [])
        .map((pagina) => {
          const secoes = (pagina.secoes || [])
            .map((secao) => {
              const permissoes = (secao.permissoes || []).filter((p) =>
                aceita(p, secao, pagina, modulo)
              );
              return permissoes.length ? { ...secao, permissoes } : null;
            })
            .filter(Boolean);
          return secoes.length ? { ...pagina, secoes } : null;
        })
        .filter(Boolean);
      return paginas.length ? { ...modulo, paginas } : null;
    })
    .filter(Boolean);
}

/**
 * Filtra a arvore pela busca (sem acento, sem caixa). Match no label de um
 * nivel mantem TODOS os descendentes; match numa permissao (codigo, rotulo,
 * descricao ou texto de ajuda) mantem so ela.
 */
export function filtrarModulos(modulos, termo) {
  const termoNormalizado = normalizarBusca(termo);
  if (!termoNormalizado) return modulos;
  return podarArvore(
    modulos,
    (p, secao, pagina, modulo) =>
      textoCasa(modulo.label, termoNormalizado) ||
      textoCasa(pagina.label, termoNormalizado) ||
      textoCasa(secao.label, termoNormalizado) ||
      permissaoCasa(p, termoNormalizado)
  );
}

/** "Só o que está liberado": esconde as permissoes nao concedidas. */
export function filtrarConcedidas(modulos, sets) {
  return podarArvore(modulos, (p) => estaConcedida(p.codigo, sets));
}

/**
 * Indice codigo -> { permissao, modulo, pagina } — alimenta o "Onde fica" e
 * o "Costuma andar junto com" do balao de ajuda.
 */
export function indexarPorCodigo(modulos) {
  const indice = {};
  (modulos || []).forEach((modulo) => {
    (modulo.paginas || []).forEach((pagina) => {
      permissoesDaPagina(pagina).forEach((permissao) => {
        indice[permissao.codigo] = {
          permissao,
          modulo: modulo.label,
          pagina: pagina.label,
        };
      });
    });
  });
  return indice;
}

/**
 * `codigo -> nome legivel` para telas que listam permissoes fora da matriz
 * (ajustes individuais, conflitos SoD): o mesmo nome que a matriz mostra.
 */
export function criarNomePermissao(modulos) {
  const indice = indexarPorCodigo(modulos);
  return (codigo) =>
    indice[codigo] ? rotuloPermissao(indice[codigo].permissao) : codigo;
}

/**
 * Secao "Geral" unica nao mostra label (evita ruido visual quando a pagina
 * nao tem subdivisao real).
 */
export function esconderLabelSecao(pagina, secao) {
  const secoes = pagina?.secoes || [];
  if (secoes.length !== 1) return false;
  const label = String(secao?.label || "").trim().toLowerCase();
  return !label || label === "geral";
}

/**
 * Rotulo do item por codigo dentro de UMA secao. Normalmente o rotulo proprio
 * da permissao; se dois itens da secao acabarem com o MESMO texto (rotulo
 * ausente, cai no verbo da acao), o texto sozinho ficaria ambiguo — nesses
 * casos cai para o codigo completo (`recurso:acao`).
 */
export function rotulosChipsDaSecao(permissoes) {
  const contagem = {};
  (permissoes || []).forEach((p) => {
    const r = rotuloPermissao(p);
    contagem[r] = (contagem[r] || 0) + 1;
  });
  const mapa = {};
  (permissoes || []).forEach((p) => {
    const r = rotuloPermissao(p);
    mapa[p.codigo] = contagem[r] > 1 ? p.codigo : r;
  });
  return mapa;
}
